import React, { useEffect, useState } from "react";
import { styled } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  PrimaryColor,
  TableBodyGrey,
  TableHeaderGrey,
  TableRowHover,
} from "../../../shared/styles/colors";
import { DatabaseEntities } from "../../../model/enum/database-entities.enum";
import { Typography, Button } from "@material-ui/core";
import { TablePagination } from "@mui/material";
import { Spinner } from "../../../components";
import { ToastContainer } from "react-toastify";
import { useSearchParams } from "react-router-dom";
import ApiService from "../../../service/ApiService";
import Employee from "../../../model/employee";
import EmployeeModal from "./EmployeeModal";
import CachedIcon from "@mui/icons-material/Cached";
import DateConverter from "../../../utils/DateConverter";
import EmployeeDetailModal from "./EmployeeDetailModal";
import useStyles from "../../styles/cardStyles";
import Pathnames from "../../../model/enum/Pathnames";
import PageResultResponse from "../../../model/page-result-response";
import Misc from "../../../utils/Misc";

const StyledTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: TableHeaderGrey,
    color: PrimaryColor,
    fontWeight: 700,
    whiteSpace: "nowrap",
    zIndex: 0,
    position: "static",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    color: TableBodyGrey,
    fontFamily: "monospace",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  // hide last border
  "&:last-child td, &:last-child th": {
    border: 0,
  },
  "&:hover": {
    backgroundColor: TableRowHover,
  },
}));

const Employees = () => {
  const classes = useStyles();
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();

  // Update the URL with the current page and rowsPerPage
  useEffect(() => {
    const url = new URL(window.location.href);
    url.searchParams.set("page", page.toString());
    url.searchParams.set("rowsPerPage", rowsPerPage.toString());
    window.history.pushState({}, Pathnames.APPOINTMENTS_LIST, url);
  });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      searchParams.set("page", page.toString());
      searchParams.set("rowsPerPage", rowsPerPage.toString());

      await ApiService.get<PageResultResponse>(
        `${DatabaseEntities.EMPLOYES}?${searchParams.toString()}`,
      )
        .then((data: PageResultResponse) => {
          if (Misc.isArrayEmpty(data.items)) return;
          setEmployees(data.items);
          setTotalPages(data.count);
        })
        .finally(() => {
          setLoading(false);
        });
    };

    if (loading && Misc.isArrayEmpty(employees)) fetchData();
  }, [employees]);

  const resetList = (): void => {
    setPage(1);
    setRowsPerPage(10);
    setEmployees([]);
    setLoading(true);
    if (searchParams.has("employeeId")) {
      searchParams.delete("employeeId");
      setSearchParams(searchParams);
    }
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown> | null,
    pg: number,
  ) => {
    setPage(pg + 1);
    scrollToTop();
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    let row = parseInt(event.target.value);
    setRowsPerPage(row);
    scrollToTop();
  };

  const scrollToTop = () => {
    setEmployees([]);
    setLoading(true);
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  };

  const addIdToQueryPath = (id?: number) => {
    if (!id) return;

    const url = new URL(window.location.href);
    url.searchParams.set("employeeId", id.toString());
    window.history.pushState({}, Pathnames.APPOINTMENTS_LIST, url);
  };

  const removeIdFromQueryPath = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete("employeeId");
    window.history.replaceState({}, "", url.pathname + url.search);
  };

  return (
    <>
      <div className={classes.headerContainer}>
        <Typography className={classes.title}></Typography>
        <div className={classes.headerDiv}>
          <EmployeeModal resetList={resetList} />
          <Button onClick={resetList}>
            <CachedIcon />
          </Button>
        </div>
      </div>
      <Paper sx={{ marginTop: "20px" }}>
        <TableContainer>
          <Table
            stickyHeader
            aria-label="customized table"
            padding="normal"
            size="medium"
          >
            <TableHead>
              <TableRow>
                <StyledTableCell align="right">Id</StyledTableCell>
                <StyledTableCell align="right">Name</StyledTableCell>
                <StyledTableCell align="right">Email</StyledTableCell>
                <StyledTableCell align="right">Phone</StyledTableCell>
                <StyledTableCell align="right">Address</StyledTableCell>
                <StyledTableCell align="right">Rating</StyledTableCell>
                <StyledTableCell align="right">Created At</StyledTableCell>
                <StyledTableCell align="right">Updated At</StyledTableCell>
                <StyledTableCell align="right">
                  <MoreVertIcon />
                </StyledTableCell>
              </TableRow>
            </TableHead>
            {loading ? (
              <Spinner />
            ) : (
              <TableBody>
                {employees.length > 0 ? (
                  employees.map((row: Employee) => (
                    <StyledTableRow key={row.id}>
                      <StyledTableCell align="right">{row.id}</StyledTableCell>
                      <StyledTableCell align="right">
                        {row.firstName} {row.lastName}
                      </StyledTableCell>
                      <StyledTableCell align="right">
                        {row.email}
                      </StyledTableCell>
                      <StyledTableCell align="right">
                        {Misc.formatPhoneNumber(row.phone)}
                      </StyledTableCell>
                      <StyledTableCell align="right">
                        {row.street &&
                          `${row.street}, ${row.city}, ${row.state}, ${row.zip}`}
                      </StyledTableCell>
                      <StyledTableCell align="right">
                        {row.rating}
                      </StyledTableCell>
                      <StyledTableCell align="right">
                        {DateConverter.toFullDate(row.createdAt)}
                      </StyledTableCell>
                      <StyledTableCell align="right">
                        {DateConverter.toFullDate(row.updatedAt)}
                      </StyledTableCell>
                      <StyledTableCell align="right">
                        <EmployeeDetailModal
                          employee={row}
                          resetList={resetList}
                          onOpen={() => {
                            addIdToQueryPath(row?.id);
                          }}
                          onClose={() => {
                            removeIdFromQueryPath();
                          }}
                        />
                      </StyledTableCell>
                    </StyledTableRow>
                  ))
                ) : (
                  <StyledTableCell align="left">No items</StyledTableCell>
                )}
              </TableBody>
            )}
          </Table>
        </TableContainer>
      </Paper>
      <TablePagination
        component="div"
        count={totalPages}
        page={page - 1}
        rowsPerPage={rowsPerPage}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      <ToastContainer />
    </>
  );
};

export default Employees;
